import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
// import { formatCryptoAmount } from "../../utils/format";
import { getExchangeRate } from "../../../redux/actions/Coingecko/getExchangeRate";
// import { FormatBalance } from "../../../helper";

/**
 * Custom hook to handle exchange rate fetching, unit conversions, and input switching.
 */
export const useExchangeRate = ({
  fiatCode = "USD",
  cryptoCode = "BTC",
  isSell = false,
  amount = "",
  onAmountChange,
  inputMode = "fiat",
  setInputMode,
  cryptoDecimal = null
}) => {
  const dispatch = useDispatch();
  const [exchangeRateData, setExchangeRateData] = useState(null);
  const [isRateLoading, setIsRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);

  // Fetch exchange rate when assets or direction change
  const fetchRate = useCallback(async () => {
    if (!fiatCode || !cryptoCode) return;
    setIsRateLoading(true);
    setRateError(null);

    try {
      const params = isSell
        ? { from_currency: cryptoCode.toLowerCase(), to_currency: fiatCode.toLowerCase() }
        : { from_currency: fiatCode.toLowerCase(), to_currency: cryptoCode.toLowerCase() };

      const res = await dispatch(getExchangeRate(params));
      const rateInfo = res?.data || res;
      setExchangeRateData(rateInfo);
    } catch (err) {
      console.error("Exchange rate fetch error:", err);
      setRateError(err);
    } finally {
      setIsRateLoading(false);
    }
  }, [dispatch, fiatCode, cryptoCode, isSell]);

  useEffect(() => {
    fetchRate();
  }, [fetchRate]);

  // Derive unit price (Fiat price per 1 unit of Crypto)
  const unitPrice = useMemo(() => {
    if (!exchangeRateData) return 1;

    if (exchangeRateData.unit_price) {
      return Number(exchangeRateData.unit_price);
    }

    const fromCurr = exchangeRateData.from_currency?.toUpperCase();
    const rate = Number(exchangeRateData.exchange_rate) || 1;

    if (fromCurr === fiatCode.toUpperCase()) {
      return rate > 0 ? 1 / rate : 1;
    } else {
      return rate;
    }
  }, [exchangeRateData, fiatCode]);

  // Calculate conversions and formatted labels
  const { primaryUnit, conversionLabel, convertedRawValue } = useMemo(() => {
    const rawValue = parseFloat(amount) || 0;

    if (inputMode === "fiat") {
      const cryptoVal = unitPrice > 0 ? rawValue / unitPrice : 0;
      const rawString = cryptoVal > 0 ? Number(cryptoVal.toFixed(cryptoDecimal || 8)).toString() : "";

      return {
        primaryUnit: fiatCode,
        // conversionLabel: `${formatCryptoAmount(cryptoVal)} ${cryptoCode}`,
        // conversionLabel: `${FormatBalance(cryptoVal, false)} ${cryptoCode}`,
        // conversionLabel: `${cryptoVal} ${cryptoCode}`,
        conversionLabel: `${Number(cryptoVal).toFixed(cryptoDecimal || 8)} ${cryptoCode}`,
        convertedRawValue: rawString,
      };
    } else {
      const fiatVal = rawValue * unitPrice;
      // const formattedFiat = fiatVal;
      const formattedFiat = fiatVal.toLocaleString("en-US", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
      const rawString = fiatVal > 0 ? Number(fiatVal.toFixed(4)).toString() : "";

      return {
        primaryUnit: cryptoCode,
        conversionLabel: `${formattedFiat} ${fiatCode}`,
        convertedRawValue: rawString,
      };
    }
  }, [amount, inputMode, fiatCode, cryptoCode, unitPrice, cryptoDecimal]);

  // Switch between Fiat <-> Crypto and convert entered number
  const toggleInputMode = useCallback(() => {
    if (convertedRawValue && parseFloat(convertedRawValue) > 0) {
      onAmountChange?.(convertedRawValue);
    }
    setInputMode?.((prevMode) => (prevMode === "fiat" ? "crypto" : "fiat"));
  }, [convertedRawValue, onAmountChange, setInputMode]);

  // Clear amount back to 0
  const handleClear = useCallback(() => {
    onAmountChange?.("");
  }, [onAmountChange]);

  // Set maximum available balance
  const handleMax = useCallback(
    (accountBalance) => {
      if (accountBalance === undefined || accountBalance === null) return;
      const rawBalance = parseFloat(accountBalance) || 0;

      if (isSell) {
        if (inputMode === "crypto") {
          onAmountChange?.(rawBalance.toString());
        } else {
          const fiatVal = rawBalance * unitPrice;
          onAmountChange?.(fiatVal.toFixed(2));
        }
      } else {
        if (inputMode === "fiat") {
          onAmountChange?.(rawBalance.toString());
        } else {
          const cryptoVal = unitPrice > 0 ? rawBalance / unitPrice : 0;
          onAmountChange?.(cryptoVal.toString());
        }
      }
    },
    [isSell, inputMode, unitPrice, onAmountChange]
  );

  return {
    exchangeRateData,
    isRateLoading,
    rateError,
    unitPrice,
    primaryUnit,
    conversionLabel,
    convertedRawValue,
    toggleInputMode,
    handleClear,
    handleMax,
    refetchRate: fetchRate,
  };
};